import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POST_TYPES = [
  { key: "question", label: "Question", icon: "🤔", color: "#3B82F6" },
  { key: "win",      label: "Win",      icon: "💪", color: "#D4AF37" },
  { key: "vent",     label: "Vent",     icon: "😤", color: "#DC2626" },
  { key: "recipe",   label: "Recipe",   icon: "🍳", color: "#10B981" },
];

export default function NewPostModal({ open, onClose, onCreated }) {
  const [postType, setPostType] = useState("win");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPostType("win"); setTitle(""); setBody("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/forums/quota`);
        setQuota(data);
      } catch {
        setQuota(null);
      }
    })();
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/forums/posts`, { post_type: postType, title, body });
      toast.success("Posted to forums");
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) ? detail.message : (detail || "Failed to post");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const overQuota = quota && !quota.is_premium && quota.remaining_this_week <= 0;

  return (
    <div
      data-testid="forums-new-post-modal"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border-2 border-[#D4AF37] w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">Create Post</div>
            <div className="font-display text-2xl mt-1">New Forum Post</div>
          </div>
          <button data-testid="forums-modal-close" onClick={onClose} className="text-neutral-400 hover:text-white text-2xl">×</button>
        </div>

        {quota && (
          <div className="px-5 py-2 text-xs text-neutral-400 border-b border-zinc-900 bg-[#0F0F0F]">
            {quota.is_premium ? (
              <span className="text-[#D4AF37] font-bold">🐦‍🔥 Premium · Unlimited posts</span>
            ) : (
              <>
                <span className={overQuota ? "text-[#DC2626] font-bold" : "text-white font-bold"}>
                  {quota.remaining_this_week}
                </span>
                <span> of {quota.free_per_week} free posts left this week</span>
              </>
            )}
          </div>
        )}

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">Post Type</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {POST_TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`forums-modal-type-${t.key}`}
                  onClick={() => setPostType(t.key)}
                  className={
                    "flex flex-col items-center gap-1 py-3 border-2 transition-colors " +
                    (postType === t.key
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-zinc-800 hover:border-[#D4AF37]/50")
                  }
                  style={postType === t.key ? { borderColor: t.color } : {}}
                >
                  <span className="text-2xl">{t.icon}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">
              Title <span className="text-neutral-700">({title.length}/140)</span>
            </div>
            <input
              data-testid="forums-modal-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 140))}
              placeholder="A clear, scannable headline…"
              className="w-full bg-[#0F0F0F] border-2 border-zinc-800 focus:border-[#D4AF37] px-3 py-2 text-white outline-none"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">
              Body <span className="text-neutral-700">({body.length}/6000)</span>
            </div>
            <textarea
              data-testid="forums-modal-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 6000))}
              rows={8}
              placeholder="Share details, context, or your full story. Markdown not yet supported."
              className="w-full bg-[#0F0F0F] border-2 border-zinc-800 focus:border-[#D4AF37] px-3 py-2 text-white outline-none resize-y"
            />
          </div>
        </div>

        <div className="border-t border-zinc-900 px-5 py-4 flex items-center justify-end gap-2">
          <button data-testid="forums-modal-cancel" onClick={onClose} className="btn-omni btn-outline-gold">Cancel</button>
          <button
            data-testid="forums-modal-submit"
            onClick={submit}
            disabled={submitting || overQuota || !title.trim()}
            className="btn-omni btn-red disabled:opacity-40"
          >
            {submitting ? "Posting…" : overQuota ? "Quota Reached" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}
